import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { fetchMyBilling } from "../lib/billing";
import { useAuth } from "../contexts/AuthContext";

// Stripe redirects here after a successful checkout. We poll
// /api/billing/me for up to ~30s waiting for the webhook to fire and
// flip subscription_status to trialing/active. Most webhooks land in
// <2s but local Stripe dashboards can take a few seconds longer.
const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 15; // ~30s

export default function BillingSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { refresh: refreshAuth } = useAuth();
  const [billing, setBilling] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const tick = async () => {
      try {
        const b = await fetchMyBilling();
        if (cancelled) return;
        setBilling(b);
        if (b?.is_active) {
          setDone(true);
          // Refresh the auth context so any feature-flag UI updates
          // immediately across the app.
          try {
            await refreshAuth?.();
          } catch {
            /* non-fatal */
          }
          return;
        }
      } catch {
        /* keep polling */
      }
      setAttempts((a) => {
        const next = a + 1;
        if (next < MAX_ATTEMPTS) {
          timer = setTimeout(tick, POLL_INTERVAL_MS);
        } else {
          setDone(true); // give up polling but still show success
        }
        return next;
      });
    };

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tierName = billing?.tier_name || "your plan";
  const trialEnd = billing?.trial_end
    ? new Date(billing.trial_end).toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <div className="min-h-screen bg-[#f4f5f7] text-[#1c2d3d] grid place-items-center px-6 py-16" data-testid="billing-success-page">
      <div className="max-w-xl w-full bg-white rounded-2xl border border-[#4a6984]/15 shadow-lg p-8 text-center">
        {!done ? (
          <>
            <Loader2 className="h-10 w-10 mx-auto text-[#4a6984] animate-spin" />
            <h1 className="mt-5 font-black text-2xl text-[#1c2d3d]">
              Confirming your subscription…
            </h1>
            <p className="mt-2 text-sm text-[#4a6984]/85">
              Stripe is sending the receipt. This usually takes a few seconds.
            </p>
          </>
        ) : billing?.is_active ? (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-[#96ded1] grid place-items-center">
              <CheckCircle2 className="h-8 w-8 text-[#1c2d3d]" />
            </div>
            <h1 className="mt-5 font-black text-3xl text-[#1c2d3d]">
              You're in.
            </h1>
            <p className="mt-2 text-base text-[#4a6984]">
              Welcome to <strong className="text-[#1c2d3d]">Atomic Edu {tierName}</strong>.
              {billing.status === "trialing" && trialEnd && (
                <> Your free trial ends <strong>{trialEnd}</strong>.</>
              )}
            </p>
            <div className="mt-6 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#96ded1]/30 text-[#1c2d3d] text-xs font-extrabold uppercase tracking-[0.18em]">
              <Sparkles className="h-3.5 w-3.5" /> Everything unlocked
            </div>
            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                data-testid="success-start-lesson"
                asChild
                className="bg-[#1c2d3d] text-white hover:bg-[#1c2d3d]/90 h-11 px-5 rounded-lg font-extrabold"
              >
                <Link to="/">Generate a lesson</Link>
              </Button>
              <Button
                data-testid="success-dashboard"
                asChild
                variant="outline"
                className="border-[#4a6984]/25 text-[#1c2d3d] hover:bg-[#f4f5f7] h-11 px-5 rounded-lg font-extrabold"
              >
                <Link to="/dashboard">Teacher Dashboard</Link>
              </Button>
            </div>
          </>
        ) : (
          <>
            <h1 className="font-black text-2xl text-[#1c2d3d]">
              Thanks — we'll have you set up shortly.
            </h1>
            <p className="mt-3 text-sm text-[#4a6984]/85">
              Stripe sent the payment but the activation webhook hasn't landed yet.
              Refresh in a minute or two — or email us at{" "}
              <a href="mailto:info@atomicedu.ca" className="underline">
                info@atomicedu.ca
              </a>{" "}
              with this reference: <code className="text-xs">{sessionId || "—"}</code>
            </p>
            <Button
              asChild
              className="mt-6 bg-[#1c2d3d] text-white hover:bg-[#1c2d3d]/90 h-11 px-5 rounded-lg font-extrabold"
            >
              <Link to="/dashboard">Go to dashboard</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
